import { useLocation, useNavigate } from 'react-router-dom';

const SelectSchedule = () => {
    const location = useLocation();
    const { primaryId, product } = location.state || {};
    const navigate = useNavigate();
    const selectedSlot = null;

    const handleNavigateToInquire = () => {
        navigate('enquire-now', { state: { primaryId, product } });
    };

    const handleNavigateToBook = () => {
        navigate('/book-schedule', {
            state: { primaryId, product, selectedSlot },
        });
    };

    // Format price duration type - capitalize first letter, lowercase the rest
    const formatPriceDuration = (duration) => {
        if (!duration) return '';
        return duration.charAt(0).toUpperCase() + duration.slice(1).toLowerCase();
    };

    // Get and format price duration type from product data
    const priceDurationType = formatPriceDuration(product?.priceDurationType);
    sessionStorage.setItem('selectedDuration', product?.priceDurationType);

    return (
        <>
            <main
                className="flex-grow bg-gradient-to-br from-slate-50 via-white to-blue-50/30"
                style={{ paddingTop: '4rem' }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    {/* Breadcrumb */}
                    <nav className="flex items-center space-x-2 text-sm text-slate-500 mb-8">
                        <span>Workspace</span>
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            focusable="false"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                        <span className="text-slate-800 font-medium">
                            {product?.category?.name || 'Space Details'}
                        </span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-16 items-start">
                        {/* Image */}
                        <div className="space-y-6">
                            <div className="relative group">
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <img
                                    src={
                                        product?.imageContents?.[0]?.displayLink ||
                                        'https://via.placeholder.com/800x800'
                                    }
                                    alt={product?.name || 'Product Image'}
                                    className="w-full aspect-square object-cover rounded-2xl shadow-xl border border-slate-200 group-hover:shadow-2xl transition-all duration-300"
                                />
                            </div>
                            {/* Dot Indicators */}
                            <div className="flex justify-center space-x-2">
                                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                <div className="w-2 h-2 bg-slate-300 rounded-full"></div>
                                <div className="w-2 h-2 bg-slate-300 rounded-full"></div>
                            </div>
                        </div>

                        {/* Details */}
                        <div className="space-y-8">
                            <div className="space-y-4">
                                <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full border border-blue-200">
                                    <svg
                                        className="w-4 h-4 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 3v4M3 5h4m6 17v-4m-2 2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                                        />
                                    </svg>
                                    Premium Workspace
                                </div>

                                <h1 className="text-5xl font-bold text-slate-800 leading-tight">
                                    {product?.name || 'Coworking space'}
                                    <span className="block bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent mt-2">
                                        designed for collaboration
                                    </span>
                                </h1>

                                <p className="text-xl text-slate-600 leading-relaxed max-w-2xl font-medium">
                                    {product?.description ||
                                        'Meet new people without leaving the office. Get the best space for work and unleash your productivity in a collaborative environment.'}
                                </p>
                            </div>

                            {/* Features */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {/* Category */}
                                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-lg">
                                    <div className="flex items-center mb-3">
                                        <div className="p-3 bg-blue-100 rounded-xl mr-4">
                                            <svg
                                                className="w-6 h-6 text-blue-600"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                                focusable="false"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-800">
                                                Workspace Type
                                            </h3>
                                            <p className="text-slate-600">
                                                {product?.category?.name || 'Uncategorized'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Price */}
                                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-lg">
                                    <div className="flex items-center mb-3">
                                        <div className="p-3 bg-blue-100 rounded-xl mr-4">
                                            <svg
                                                className="w-6 h-6 text-blue-600"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                                focusable="false"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-800">
                                                Starting Price
                                            </h3>
                                            <p className="text-slate-800 font-bold text-lg">
                                                {product?.price?.currency
                                                    ? `${product.price.currency} ${product.price.priceAmt}`
                                                    : `$${product?.price?.priceAmt || '0'}`}
                                                {priceDurationType && (
                                                    <span className="text-sm font-normal text-slate-500 ml-1">
                                                        / {priceDurationType}
                                                    </span>
                                                )}
                                            </p>
                                            {/* Debug info - remove in production */}
                                            {!product?.priceDurationType && (
                                                <p className="text-xs text-red-500 mt-1">
                                                    Note: Price duration type not available in product data
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Delivery Type */}
                            {product?.deliveryType && (
                                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-lg">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-blue-100 rounded-xl mr-4">
                                            <svg
                                                className="w-6 h-6 text-blue-600"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                                focusable="false"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-800">
                                                Delivery Type
                                            </h3>
                                            <p className="text-slate-600">
                                                {product.deliveryType === 'SHIPPING'
                                                    ? 'Shipping'
                                                    : product.deliveryType}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Amenities */}
                            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                                    What's Included
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    {[
                                        ['📶', 'High-Speed WiFi'],
                                        ['☕', 'Complimentary Coffee'],
                                        ['🖨️', 'Printing Services'],
                                        ['🏢', 'Meeting Rooms'],
                                        ['🚗', 'Parking Available'],
                                        ['🔒', '24/7 Security'],
                                    ].map(([icon, label], i) => (
                                        <div key={i} className="flex items-center text-slate-700 font-medium">
                                            <span className="text-lg mr-3">{icon}</span>
                                            <span className="text-sm">{label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-4">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button
                                        id="book-now-button"
                                        onClick={handleNavigateToBook}
                                        className="flex-1 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 transition-all duration-200 shadow-xl hover:shadow-2xl"
                                    >
                                        <div className="flex items-center justify-center">
                                            <svg
                                                className="w-5 h-5 mr-2"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                                focusable="false"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                />
                                            </svg>
                                            Book Now
                                        </div>
                                    </button>

                                    <button
                                        id="get-in-touch-button"
                                        onClick={handleNavigateToInquire}
                                        className="flex-1 px-8 py-4 bg-white border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:border-slate-400 hover:bg-slate-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
                                    >
                                        <div className="flex items-center justify-center">
                                            <svg
                                                className="w-5 h-5 mr-2"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                                focusable="false"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            Get in Touch
                                        </div>
                                    </button>
                                </div>

                                <p className="text-center text-sm text-slate-500 font-medium">
                                    💡 Need help choosing? Our team is here to assist you
                                </p>
                            </div>

                            {/* Trust */}
                            <div className="flex items-center justify-center space-x-6 pt-6 border-t border-slate-200">
                                <div className="flex items-center text-slate-600">
                                    <svg
                                        className="w-5 h-5 text-blue-600 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <span className="text-sm font-medium">Instant Booking</span>
                                </div>
                                <div className="flex items-center text-slate-600">
                                    <svg
                                        className="w-5 h-5 text-blue-600 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                        />
                                    </svg>
                                    <span className="text-sm font-medium">Secure Payment</span>
                                </div>
                                <div className="flex items-center text-slate-600">
                                    <svg
                                        className="w-5 h-5 text-blue-600 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                                        />
                                    </svg>
                                    <span className="text-sm font-medium">24/7 Support</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};
export default SelectSchedule;
